import React__default, { forwardRef, useRef } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/**
 *
 * Wraps your app to provide global functionality to your tooltips.
 *
 */
const Provider = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(RadixTooltip.Provider, props, children);
};
Provider.displayName = "Provider";
/**
 *
 * Contains all the parts of a tooltip.
 *
 */
const Root = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(RadixTooltip.Root, props, children);
};
Root.displayName = "Root";
/**
 *
 * The button that toggles the tooltip. By default, the Tooltip.Content will position itself against the trigger.
 *
 * [data-state]	"closed" | "delayed-open" | "instant-open"
 *
 */
const Trigger = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(RadixTooltip.Trigger, _extends({
    ref: innerRef
  }, props), children);
});

/**
 *
 * The button that toggles the tooltip. By default, the Tooltip.Content will position itself against the trigger.
 *
 * [data-state]	"closed" | "delayed-open" | "instant-open"
 *
 */
const Portal = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(RadixTooltip.Portal, props, children);
};
Portal.displayName = "Portal";
/**
 *
 * The component that pops out when the tooltip is open.
 *
 * [data-state]	"closed" | "delayed-open" | "instant-open"
 * [data-side]	"left" | "right" | "bottom" | "top"
 * [data-align]	"start" | "end" | "center"
 *
 */
const Content = /*#__PURE__*/forwardRef(({
  children,
  forceMount = true,
  asChild = false,
  align = 'center',
  side = 'top',
  sideOffset = 0,
  alignOffset = 0,
  avoidCollisions = true,
  collisionBoundary = [],
  collisionPadding = 0,
  arrowPadding = 0,
  sticky = 'partial',
  hideWhenDetached = false,
  'aria-label': ariaLabel,
  testId,
  ...props
}, forwardedRef) => {
  const innerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(RadixTooltip.Content, _extends({
    ref: innerRef,
    asChild: asChild,
    align: align,
    side: side,
    sideOffset: sideOffset,
    alignOffset: alignOffset,
    avoidCollisions: avoidCollisions,
    collisionBoundary: collisionBoundary,
    collisionPadding: collisionPadding,
    arrowPadding: arrowPadding,
    sticky: sticky,
    hideWhenDetached: hideWhenDetached,
    "aria-label": ariaLabel,
    "data-testid": testId
  }, props), children);
});

/**
 *
 * An optional arrow element to render alongside the tooltip.
 *
 * This can be used to help visually link the trigger with the Tooltip.Content. Must be rendered inside Tooltip.Content.
 *
 */
const Arrow = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(RadixTooltip.Arrow, props, children);
};
Arrow.displayName = "Arrow";
// Exports
Provider.displayName = 'Tooltip.Provider';
Root.displayName = 'Tooltip';
Trigger.displayName = 'Tooltip.Trigger';
Portal.displayName = 'Tooltip.Portal';
Content.displayName = 'Tooltip.Content';
Arrow.displayName = 'Tooltip.Arrow';

/**
 * A popup that displays information related to an element when the element receives keyboard focus or the mouse hovers over it.
 */
var Tooltip = Object.assign(Root, {
  Provider,
  Trigger,
  Portal,
  Content,
  Arrow
});

export { Tooltip as default };
